import { STYLE_VARIANTS, type GlobalConfig } from '@repo/go-ui';
import type { HeatmapWidgetInitParams, HeatmapWidgetStateParams } from '@repo/go-widgets';
import { HeatmapWidgetState } from '@repo/go-widgets';
import { action, flow, makeObservable, observable, reaction } from 'mobx';

import { lagacyAgent } from 'src/api/legacyAgent';
import { wellLogsWidgetAgent } from 'src/api/wellLogsWidgetAgent';
import type { TGroup, TWellListCombinedData } from 'src/entities/tab/TabEntity';
import { requireService } from 'src/packages/di';

import { setWellIdInGroup } from '../../utils/setWellIdInGroup';
import type { GroupSelectStore } from '../group-select/GroupSelect.store';
import { getCorrectLocale } from '../well-day-depth-widget/utils';

import type { HeatmapWidgetEntity } from './HeatmapWidget.entity';

export class HeatmapWidgetStore {
  private readonly heatmapEntity: HeatmapWidgetEntity;
  private readonly groupSelectStore: GroupSelectStore;
  private readonly initParams: HeatmapWidgetInitParams;

  @observable isExitFullscreenFetching: boolean = false;
  @observable isReady: boolean = false;
  @observable progress: number | null = null;
  @observable isGroupSelectOpen: boolean = false;

  constructor(
    heatmapEntity: HeatmapWidgetEntity,
    groupSelectStore: GroupSelectStore,
    private readonly wellListPanel: TWellListCombinedData,
    private readonly theme = requireService('theme'),
    private readonly otherSettings = requireService('otherSettings'),
    private readonly stompCachePublisherService = requireService('stompCachePublisherService'),
    private readonly wellStatusService = requireService('wellStatusService'),
    private readonly language = requireService('language'),
    readonly classOfUnitsService = requireService('classOfUnitsService'),
    private readonly userPreferencesService = requireService('userPreference'),
    private readonly notifications = requireService('notifications'),
  ) {
    this.heatmapEntity = heatmapEntity;
    this.groupSelectStore = groupSelectStore;
    this.isGroupSelectOpen = heatmapEntity.isGroupsDefaultOpened;

    this.initParams = {
      axiosInstance: wellLogsWidgetAgent,
      legacyAxiosInstance: lagacyAgent,
      classOfUnitsService: this.classOfUnitsService,
      defaultDepthUnits:
        this.userPreferencesService.preferences.defaultDepthUnits === 'INDEX_DEPTH_SCALE_METERS' ? 'm' : 'ft',
      stompPublisher: this.stompCachePublisherService.stompPublisher,
      wellDirectoryServiceStompPublisher: this.wellStatusService,
    };

    makeObservable(this);
  }

  @action.bound
  setIsGroupSelectOpen(isOpen: boolean): void {
    this.isGroupSelectOpen = isOpen;
  }

  @action.bound
  setGroupForWellLogs(group: TGroup | null): void {
    this.heatmapEntity.setGroup(group ? group.id : null);
  }

  @flow.bound
  async *exitFullScreen(themeConfig: GlobalConfig) {
    if (this.isExitFullscreenFetching) return;

    this.isExitFullscreenFetching = true;

    this.heatmapEntity.state?.setAppParams({
      fullScreen: false,
      width: this.heatmapEntity.size.w,
      height: this.heatmapEntity.size.h,
      theme: [STYLE_VARIANTS.dark, STYLE_VARIANTS.light].includes(themeConfig.theme) ? themeConfig.theme : undefined,
    });

    yield;

    this.heatmapEntity.setFullscreen(false);

    this.isExitFullscreenFetching = false;
  }

  @action.bound
  private setReady(value: boolean): void {
    this.isReady = value;
  }

  @action.bound
  private setProgress(value: number | null) {
    this.progress = value;
  }

  @action.bound
  private setInActiveTab(value: boolean) {
    this.heatmapEntity.setInActiveTab(value);
  }

  private getNewState(wellId: number): HeatmapWidgetState {
    const stateParams = this.heatmapEntity.stateParams;

    const eventHandlers = {
      onInternalStateChanged: (state: Partial<HeatmapWidgetStateParams>) => {
        this.heatmapEntity.setStateParams({ ...this.heatmapEntity.stateParams, ...state });
      },
      onInitializedSuccess: () => {
        this.setReady(true);
      },
      onInitializationFailed: (_: unknown, error?: Error) => {
        this.setReady(false);

        console.error(error);
        this.notifications.showErrorMessageT('errors:failedToInitWidget');
      },
      onWellboreChanged: (wellboreName: string) => {
        this.heatmapEntity.setWellboreName(wellboreName);
      },
    };

    const appParams = {
      wellId,
      width: 500,
      height: 500,
      theme: this.theme.theme,
      locale: getCorrectLocale(this.language.language),
      isAltNames: this.userPreferencesService.preferences.isAltNames,
      fullScreen: this.heatmapEntity.isFullscreen,
      active: this.heatmapEntity.inActiveTab,
    };

    return new HeatmapWidgetState(appParams, this.initParams, stateParams, eventHandlers);
  }

  effect = () => {
    this.setInActiveTab(true);

    const disposeGroupId = reaction(
      () => this.heatmapEntity.groupId,
      (groupId) => {
        if (groupId === null) return;
        const widgets = this.groupSelectStore.groupManager.getWidgetsByGroupId(groupId);
        setWellIdInGroup(widgets, this.heatmapEntity, this.wellListPanel);
      },
    );

    const disposeWellId = reaction(
      () => this.heatmapEntity.wellId,
      (wellId, previousWellId) => {
        if (wellId && !this.heatmapEntity.state) {
          const newState = this.getNewState(wellId);
          this.heatmapEntity.setState(newState);
          this.setReady(true);
        } else if (wellId && this.heatmapEntity.state && previousWellId !== undefined && previousWellId !== wellId) {
          this.heatmapEntity.state.setWell(wellId);
        } else if (!wellId && this.heatmapEntity.state) {
          this.heatmapEntity.state.destroy();
          this.heatmapEntity.setState(null);
        }
        this.setProgress(null);
      },
      { fireImmediately: true, name: 'HeatmapWidgetStore.wellId' },
    );

    const disposeStateParams = reaction(
      () => ({
        theme: this.theme.theme,
        language: this.language.language,
        isAltNames: this.userPreferencesService.preferences.isAltNames,
      }),
      ({ theme, language, isAltNames }) => {
        this.heatmapEntity.state?.setAppParams({
          theme,
          locale: language as 'ru' | 'en' | 'es',
          isAltNames,
        });
      },
      { fireImmediately: true },
    );

    const wellListPanelDisposer = reaction(
      () => this.wellListPanel.store.selectedWellId,
      (wellId) => {
        if (this.wellListPanel.panelState?.groupId === this.heatmapEntity.groupId) {
          this.heatmapEntity.setWellId(wellId);
        }
      },
      { fireImmediately: true },
    );

    return () => {
      this.setInActiveTab(false);
      disposeGroupId();
      disposeWellId();
      disposeStateParams();
      wellListPanelDisposer();
    };
  };
}
